import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  Chip,
  Slide,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '500px',
    overflow: 'visible',
    borderRadius: '16px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    backgroundColor: '#ffffff',
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(10px)',
  },
}));

const WarningHeader = styled(Box)(({ theme }) => ({
  background: '#e3f2fd',
  padding: '24px',
  borderBottom: '1px solid #90caf9',
  position: 'relative',
}));

const WarningChip = styled(Chip)(({ theme }) => ({
  backgroundColor: '#e1f5fe',
  color: '#1565c0',
  fontWeight: 'bold',
  fontSize: '14px',
  border: '1px solid #42a5f5',
  '& .MuiChip-label': {
    padding: '10px 12px',
  },
  fontFamily: 'Rubik',
}));

const ProceedButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#1976d2',
  color: 'white',
  fontWeight: 'bold',
  padding: '12px 24px',
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '16px',
  '&:hover': {
    backgroundColor: '#1565c0',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.3)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  transition: 'all 0.2s ease-in-out',
  width: '100%',
  fontFamily: 'Rubik',
}));

const CancelButton = styled(Button)(({ theme, disabled }) => ({
  color: '#666',
  backgroundColor: '#f1f1f1',
  fontWeight: '600',
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '16px',
  border: '1px solid #ccc',
  opacity: disabled ? 0.6 : 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
  '&:hover': {
    backgroundColor: disabled ? '#f1f1f1' : '#e0e0e0',
    borderColor: disabled ? '#ccc' : '#aaa',
  },
  transition: 'all 0.2s ease-in-out',
  padding: '0.5rem 1.2rem',
  fontFamily: 'Rubik',
  width: '100%',
}));

const AadhaarTDSWarningModal = ({ open, onClose, onProceed, isLoading }) => {
  const isMobile = useMediaQuery('(max-width:1000px)');
  
  if (!open) return null;
  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      aria-labelledby="aadhaar-warning-title"
    >
      <WarningHeader>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                backgroundColor: '#e3f2fd',
                padding: '5px',
                borderRadius: '50%',
                border: '2px solid #1976d2',
              }}
            >
              <WarningIcon sx={{ color: '#1976d2', fontSize: 28 }} />
            </Box>
            <Typography
              id="aadhaar-warning-title"
              sx={{
                fontWeight: 'bold',
                color: '#333',
                fontSize: '22px',
                padding: '1px 0',
                fontFamily: 'Rubik',
              }}
            >
              Aadhaar not linked with your PAN!
            </Typography>
          </Box>
          {!isMobile && (
            <IconButton onClick={onClose}>
              <CloseIcon sx={{ color: '#666' }} />
            </IconButton>
          )}
        </Box>
      </WarningHeader>

      <DialogContent sx={{ padding: '24px' }}>
        <Alert
          severity="warning"
          sx={{
            backgroundColor: '#f66300',
            border: '1px solid rgb(122, 36, 15)',
            borderRadius: '12px',
            marginBottom: '16px',
            '& .MuiAlert-icon': {
              color: '#ffff',
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#ffff',
              lineHeight: 1.6,
              fontSize: '15px',
              fontFamily: 'Rubik',
            }}
          >
            Your Aadhaar is not linked with this PAN. Hence{' '}
            <WarningChip label="20% TDS will be deducted" />
          </Typography>
        </Alert>

        <Typography
          variant="body1"
          sx={{
            color: '#555',
            fontWeight: '700',
            marginTop: '12px',
            fontFamily: 'Rubik',
            fontSize:'15px'
          }}
        >
          Do you want to proceed with the redeem process?
        </Typography>
      </DialogContent>

      <DialogActions sx={{ padding: '24px', gap: '12px' }}>
        <CancelButton onClick={onClose} disabled={isLoading}>
          Cancel
        </CancelButton>

        <ProceedButton
          onClick={onProceed}
          disabled={isLoading}
          style={{
            opacity: isLoading ? 0.7 : 1,
            minWidth: '120px',
          }}
        >
          {isLoading ? (
            <Box display="flex" alignItems="center" gap="8px">
              <CircularProgress size={18} style={{ color: 'white' }} />
              {isMobile ? '' : 'Processing...'}
            </Box>
          ) : (
            'Proceed'
          )}
        </ProceedButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default AadhaarTDSWarningModal;
